import {
  captureEvent,
  identifyUser,
  resetUser,
  FUNNEL_EVENTS,
} from '@navio-maritime/platform/observability';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { supabaseAuth } from '@/lib/supabase';
import { setSupabaseTokenGetter } from '@/lib/trpc';

import type { Session, User } from '@supabase/supabase-js';
import type { ReactNode } from 'react';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const AUTH_TIMEOUT_MS = 15_000;

async function withAuthTimeout<T>(operation: Promise<T>, message: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), AUTH_TIMEOUT_MS);
  });

  try {
    return await Promise.race([operation, timeout]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error('Authentication failed');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { session, loading } = useAuthSession();

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await withAuthTimeout(
        supabaseAuth.auth.signInWithPassword({ email, password }),
        'Authentication service did not respond'
      );
      return { error };
    } catch (error) {
      return { error: toError(error) };
    }
  };

  const signOut = async () => {
    await withAuthTimeout(supabaseAuth.auth.signOut(), 'Authentication service did not respond');
  };

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    loading,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuthSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const identifiedUserId = useRef<string | null>(null);

  useEffect(() => {
    const syncAnalyticsIdentity = (nextSession: Session | null) => {
      identifiedUserId.current = syncAnalyticsUser(nextSession, identifiedUserId.current);
    };

    setSupabaseTokenGetter(getCurrentAccessToken);
    void loadInitialSession(setSession, setLoading, syncAnalyticsIdentity);

    const { data: sub } = supabaseAuth.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      syncAnalyticsIdentity(newSession);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  return { session, loading };
}

async function getCurrentAccessToken() {
  const { data } = await withAuthTimeout(
    supabaseAuth.auth.getSession(),
    'Authentication service did not respond'
  );
  return data.session?.access_token ?? null;
}

async function loadInitialSession(
  setSession: (session: Session | null) => void,
  setLoading: (loading: boolean) => void,
  syncAnalyticsIdentity: (session: Session | null) => void
) {
  try {
    const { data } = await withAuthTimeout(
      supabaseAuth.auth.getSession(),
      'Authentication service did not respond'
    );
    setSession(data.session);
    syncAnalyticsIdentity(data.session);
  } catch {
    setSession(null);
  } finally {
    setLoading(false);
  }
}

function syncAnalyticsUser(nextSession: Session | null, currentUserId: string | null) {
  const nextUserId = nextSession?.user?.id ?? null;
  if (nextUserId === currentUserId) return currentUserId;

  if (nextUserId) {
    identifyUser({ id: nextUserId });
    captureEvent(FUNNEL_EVENTS.signedIn);
  } else {
    resetUser();
  }
  return nextUserId;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
